import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import errorMonitoringService from '../../services/errorMonitoringService';

import AIAnalysisDashboard from './components/AIAnalysisDashboard';
import ConversationalAIWorkspace from './components/ConversationalAIWorkspace';
import PatternRecognitionPanel from './components/PatternRecognitionPanel';
import SolutionRankingPanel from './components/SolutionRankingPanel';
import KnowledgeBasePanel from './components/KnowledgeBasePanel';

const AnthropicAIRootCauseAnalysisEngine = () => {
  const [activeTab, setActiveTab] = useState('ai_conversation');
  const [errors, setErrors] = useState([]);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [activeAnalyses, setActiveAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [dashboardMetrics, setDashboardMetrics] = useState({
    activeAnalyses: 0,
    avgConfidenceScore: 0,
    resolutionSuccessRate: 0,
    learningModelPerformance: 0
  });

  useEffect(() => {
    loadDashboardData();

    // Real-time error subscription
    const errorChannel = errorMonitoringService?.subscribeToErrors((newError) => {
      setErrors(prev => [newError, ...prev]);
      loadDashboardData();
    });

    return () => {
      errorMonitoringService?.unsubscribeFromErrors(errorChannel);
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const [errorsData, statsData] = await Promise.all([
        errorMonitoringService?.getAllErrors({ limit: 50, errorStatus: 'new' }),
        errorMonitoringService?.getErrorStatistics()
      ]);

      setErrors(errorsData || []);

      // Calculate dashboard metrics
      const activeCount = errorsData?.filter(e => e?.errorStatus === 'new')?.length || 0;
      const avgConfidence = 85.4; // Mock for now - would come from AI analysis results
      const successRate = 92.3; // Mock for now - would come from resolution tracking
      const modelPerformance = 88.7; // Mock for now - would come from ML model metrics

      setDashboardMetrics({
        activeAnalyses: activeCount,
        avgConfidenceScore: avgConfidence,
        resolutionSuccessRate: successRate,
        learningModelPerformance: modelPerformance
      });
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboardData();
  };

  const handleErrorSelect = (error) => {
    setSelectedError(error);
    setActiveTab('ai_conversation');
  };

  const handleAnalysisComplete = (analysis) => {
    setAnalysisHistory(prev => [analysis, ...prev]);
    loadDashboardData();
  };

  const tabs = [
    { id: 'ai_conversation', label: 'AI Conversation', icon: 'MessageSquare' },
    { id: 'pattern_recognition', label: 'Pattern Recognition', icon: 'TrendingUp' },
    { id: 'solution_ranking', label: 'Solution Ranking', icon: 'Award' },
    { id: 'knowledge_base', label: 'Knowledge Base', icon: 'BookOpen' }
  ];

  return (
    <>
      <Helmet>
        <title>Anthropic AI Root Cause Analysis Engine | PNG HR Compliance Platform</title>
        <meta name="description" content="AI-powered root cause analysis engine with intelligent error diagnosis, automated pattern recognition, and conversational AI interface for complex system failures" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
        <Header />
        <GlobalFilterBar onFilterChange={setGlobalFilter} />
        <ComplianceAlertBanner />

        <main className="container mx-auto px-4 py-6">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
                  <Icon name="Brain" className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Anthropic AI Root Cause Analysis Engine</h1>
                  <p className="text-gray-600 mt-1">Intelligent error diagnosis with AI-powered root cause analysis and automated resolution recommendations</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <DataRefreshIndicator isRefreshing={refreshing} />
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  disabled={refreshing}
                  className="flex items-center gap-2"
                >
                  <Icon name="RefreshCw" className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-800 font-medium">Error Loading Data</p>
                <p className="text-red-600 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Main Content Grid: 12 columns (4 + 8) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Sidebar: AI Analysis Dashboard (4 columns) */}
            <div className="lg:col-span-4">
              <AIAnalysisDashboard
                metrics={dashboardMetrics}
                errors={errors}
                activeAnalyses={activeAnalyses}
                onErrorSelect={handleErrorSelect}
                loading={loading}
              />
            </div>

            {/* Main Workspace (8 columns) */}
            <div className="lg:col-span-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                {/* Tab Navigation */}
                <div className="border-b border-gray-200 px-6 pt-4">
                  <div className="flex gap-1 overflow-x-auto">
                    {tabs?.map(tab => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`flex items-center gap-2 px-4 py-3 font-medium text-sm rounded-t-lg transition-all whitespace-nowrap ${
                          activeTab === tab?.id
                            ? 'bg-white text-purple-600 border-b-2 border-purple-600' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <Icon name={tab?.icon} className="w-4 h-4" />
                        {tab?.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <Icon name="Loader" className="w-8 h-8 text-purple-600 animate-spin mx-auto mb-3" />
                        <p className="text-gray-600">Loading AI analysis engine...</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {activeTab === 'ai_conversation' && (
                        <ConversationalAIWorkspace
                          selectedError={selectedError}
                          errors={errors}
                          onAnalysisComplete={handleAnalysisComplete}
                        />
                      )}
                      {activeTab === 'pattern_recognition' && (
                        <PatternRecognitionPanel
                          errors={errors}
                          analysisHistory={analysisHistory}
                        />
                      )}
                      {activeTab === 'solution_ranking' && (
                        <SolutionRankingPanel
                          selectedError={selectedError}
                          errors={errors}
                        />
                      )}
                      {activeTab === 'knowledge_base' && (
                        <KnowledgeBasePanel
                          analysisHistory={analysisHistory}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AnthropicAIRootCauseAnalysisEngine;